import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { InputNumber } from "antd"
import classNames from "classnames"
import styles from "./CountOfTotalInput.module.css"

// InputNumber that shows the total inline right after the typed count ("8 /10").
// An invisible copy of the input's text inside the overlay pushes "/total" to sit just past the digits.
// The copy mirrors what the input displays, not the form value: an out-of-range entry (e.g. 0 with
// min 1) is shown by InputNumber but never reaches onChange, and on blur it gets clamped internally.
export const CountOfTotalInput = ({ value, total, className, ...props }) => {
  const inputRef = useRef(null)
  const blurFrameRef = useRef(null)
  const [displayText, setDisplayText] = useState("")

  const syncDisplayText = () => setDisplayText(inputRef.current?.value ?? "")

  // InputNumber copies a new `value` prop into its text in its own follow-up render, after this
  // effect has run, so read again on the next frame too (e.g. a readOnly field driven by another input).
  useLayoutEffect(() => {
    syncDisplayText()
    const frameId = requestAnimationFrame(syncDisplayText)
    return () => cancelAnimationFrame(frameId)
  })

  // The blur frame can outlive the input (e.g. Cancel blurs it, then the modal unmounts it).
  useEffect(() => () => cancelAnimationFrame(blurFrameRef.current), [])

  return (
    <div
      className={classNames(styles.wrapper, className)}
      onInput={syncDisplayText}
      // InputNumber reformats/clamps its text after blur, so read it on the next frame.
      onBlur={() => {
        blurFrameRef.current = requestAnimationFrame(syncDisplayText)
      }}
    >
      <InputNumber
        size="large"
        {...props}
        ref={inputRef}
        value={value}
        controls={props.readOnly ? false : props.controls}
        className={classNames(styles.input, props.readOnly && styles.readOnly)}
      />
      {total ? (
        <span className={styles.overlay} aria-hidden="true">
          <span className={styles.ghost}>{displayText}</span>
          <span className={styles.total}>/{total}</span>
        </span>
      ) : null}
    </div>
  )
}

CountOfTotalInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.number,
  readOnly: PropTypes.bool,
  controls: PropTypes.bool,
  className: PropTypes.string
}
